import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, Collapse, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton } from "../../../components/form";
import { anomalyService } from "../../../services/anomalyService";
import { openapiCatalogService } from "../../../services/openapiCatalogService";
import type { AnomalyMutationDraft } from "../../../types/anomaly";
import type { ChatOrchestrationWebSocketPayload } from "../../../services/chatService";
import { recipientDomainNotAllowedText } from "../../../utils/notification";

function isOpenApiDraft(draft: AnomalyMutationDraft | null): boolean {
  if (!draft) return false;
  if (String(draft.targetType || "").toUpperCase() === "OPENAPI") return true;
  const extra = draft as AnomalyMutationDraft & { catalogId?: string; catalogName?: string };
  return Boolean(extra.catalogId || extra.catalogName);
}

function resolveMutationDraft(payload: ChatOrchestrationWebSocketPayload): AnomalyMutationDraft | null {
  if (payload.mutationDraft && typeof payload.mutationDraft === "object") {
    return payload.mutationDraft as AnomalyMutationDraft;
  }
  const result = payload.result;
  if (result && typeof result === "object") {
    const record = result as Record<string, unknown>;
    if (record.mutationDraft && typeof record.mutationDraft === "object") {
      return record.mutationDraft as AnomalyMutationDraft;
    }
    if (record.targetType && record.after) {
      return record as AnomalyMutationDraft;
    }
  }
  return null;
}

function isEmptyAfter(after: unknown): boolean {
  if (after == null) return true;
  if (typeof after !== "object") return true;
  return Object.keys(after as Record<string, unknown>).length === 0;
}

function isEmptyBefore(before: unknown): boolean {
  if (before == null) return true;
  if (typeof before !== "object") return false;
  if (Array.isArray(before)) return before.length === 0;
  return Object.keys(before as Record<string, unknown>).length === 0;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function formatRecipients(to: unknown): string {
  if (Array.isArray(to)) {
    return to.map((item) => String(item)).filter(Boolean).join(", ");
  }
  return to != null ? String(to) : "";
}

function formatFieldValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === "string" ? item : JSON.stringify(item))).join(", ") || "—";
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

const SUMMARY_KEYS = [
  "name",
  "description",
  "enabled",
  "timeField",
  "bucketInterval",
  "lookback",
  "scheduleCron",
  "indices",
  "indexPattern",
  "detectorType",
] as const;

function JsonBlock({ value, maxHeight = 280 }: { value: unknown; maxHeight?: number }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        width: "100%",
        boxSizing: "border-box",
        bgcolor: "action.hover",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "auto",
        maxHeight,
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {JSON.stringify(value ?? {}, null, 2)}
    </Box>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function PayloadSection({
  label,
  value,
  emptyHint,
}: {
  label: string;
  value: unknown;
  emptyHint?: string;
}) {
  const empty = isEmptyBefore(value) && isEmptyAfter(value);
  return (
    <Stack spacing={0.75} sx={{ width: "100%", minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      {empty && emptyHint ? (
        <Box
          sx={{
            p: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">{emptyHint}</Typography>
        </Box>
      ) : (
        <JsonBlock value={value} />
      )}
    </Stack>
  );
}

export default function MutationDraftPanel({
  message,
  payload,
}: {
  message?: string;
  payload: ChatOrchestrationWebSocketPayload;
}) {
  const { t } = useTranslation();
  const initialDraft = useMemo(() => resolveMutationDraft(payload), [payload]);
  const [draft, setDraft] = useState<AnomalyMutationDraft | null>(initialDraft);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorWarning, setErrorWarning] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const openApi = isOpenApiDraft(initialDraft);

  useEffect(() => {
    setDraft(initialDraft);
    setShowJson(false);
  }, [initialDraft]);

  // Refresh from API so Sonra shows the persisted after payload (not a stale/empty chat copy).
  useEffect(() => {
    const id = initialDraft?.id;
    if (!id) return;
    let cancelled = false;
    const loader = openApi ? openapiCatalogService.getDraft(id) : anomalyService.getDraft(id);
    void loader
      .then((fresh) => {
        if (!cancelled) setDraft(fresh as AnomalyMutationDraft);
      })
      .catch(() => {
        /* keep chat payload if refresh fails */
      });
    return () => {
      cancelled = true;
    };
  }, [initialDraft?.id, openApi]);

  const isMail = String(draft?.targetType || "").toUpperCase() === "MAIL";
  const afterRecord = !isMail ? asRecord(draft?.after) : undefined;
  const summaryFields = useMemo(() => {
    if (!afterRecord) return [];
    return SUMMARY_KEYS.filter((key) => afterRecord[key] != null && afterRecord[key] !== "").map(
      (key) => ({ key, value: formatFieldValue(afterRecord[key]) })
    );
  }, [afterRecord]);

  if (!draft) {
    return (
      <Alert severity="warning">{t("anomaly.mutationDraftMissing")}</Alert>
    );
  }

  const applied = status === "APPLIED" || draft.status === "APPLIED";
  const rejected = status === "REJECTED" || draft.status === "REJECTED";
  const pending = !applied && !rejected;
  const afterEmpty = isEmptyAfter(draft.after);
  const beforeEmpty = isEmptyBefore(draft.before);
  const createNeedsAfter =
    (draft.operation || "update").toLowerCase() === "create" && afterEmpty;
  const canApprove = Boolean(draft.id) && !createNeedsAfter && !afterEmpty;
  const mailAfter = isMail ? asRecord(draft.after) : undefined;
  const openApiExtra = draft as AnomalyMutationDraft & {
    catalogId?: string;
    catalogName?: string;
    tag?: string;
    toolName?: string;
    applyResult?: string;
  };

  const targetLabel = openApi
    ? `${openApiExtra.catalogName || openApiExtra.catalogId || draft.targetIndex || "openapi"}${
        openApiExtra.tag ? ` / ${openApiExtra.tag}` : ""
      }${
        openApiExtra.toolName || draft.targetId
          ? ` / ${openApiExtra.toolName || draft.targetId}`
          : ""
      }`
    : [draft.targetType, draft.targetIndex, draft.targetId].filter(Boolean).join(" / ");

  const apply = async () => {
    if (!draft.id || !canApprove) return;
    setBusy(true);
    setError(null);
    setErrorWarning(false);
    try {
      const result = openApi
        ? await openapiCatalogService.applyDraft(draft.id)
        : await anomalyService.applyDraft(draft.id);
      setStatus(result.status || "APPLIED");
      setDraft(result as AnomalyMutationDraft);
    } catch (e) {
      const warning = recipientDomainNotAllowedText((e as { response?: { data?: unknown } })?.response?.data);
      setErrorWarning(Boolean(warning));
      setError(warning || (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!draft.id) return;
    setBusy(true);
    setError(null);
    setErrorWarning(false);
    try {
      const result = openApi
        ? await openapiCatalogService.rejectDraft(draft.id)
        : await anomalyService.rejectDraft(draft.id);
      setStatus(result.status || "REJECTED");
      setDraft(result as AnomalyMutationDraft);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={1.75}>
        <Stack spacing={1}>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {message ||
              draft.summary ||
              (isMail
                ? t("chat.mail.draftTitle")
                : openApi
                  ? t("openapiCatalog.mutationDraftTitle")
                  : t("anomaly.mutationDraftTitle"))}
          </Typography>

          {!isMail && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={targetLabel || t("anomaly.mutationTarget")} variant="outlined" />
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={(draft.operation || "update").toLowerCase()}
              />
            </Stack>
          )}
        </Stack>

        {isMail && mailAfter ? (
          <Stack spacing={1.5}>
            <DetailField label={t("chat.mail.to")} value={formatRecipients(mailAfter.to) || "—"} />
            <DetailField label={t("chat.mail.subject")} value={String(mailAfter.subject || "—")} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("chat.mail.body")}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1.25,
                  bgcolor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: 280,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {String(mailAfter.body ?? "")}
              </Box>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              {beforeEmpty ? t("anomaly.mutationProposed") : t("anomaly.mutationCompare")}
            </Typography>

            {summaryFields.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.25,
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                {summaryFields.map((field) => (
                  <DetailField key={field.key} label={field.key} value={field.value} />
                ))}
              </Box>
            )}

            <Stack direction="row" justifyContent="flex-start">
              <AppButton size="small" variant="text" onClick={() => setShowJson((v) => !v)}>
                {showJson ? t("anomaly.mutationHideJson") : t("anomaly.mutationShowJson")}
              </AppButton>
            </Stack>

            <Collapse in={showJson || summaryFields.length === 0}>
              <Stack spacing={1.5}>
                {beforeEmpty ? (
                  <PayloadSection label={t("anomaly.after")} value={draft.after} />
                ) : (
                  <>
                    <PayloadSection
                      label={t("anomaly.before")}
                      value={draft.before}
                      emptyHint={t("anomaly.mutationEmptyBefore")}
                    />
                    <Divider />
                    <PayloadSection label={t("anomaly.after")} value={draft.after} />
                  </>
                )}
              </Stack>
            </Collapse>
          </Stack>
        )}

        {createNeedsAfter && (
          <Alert severity="warning">{t("anomaly.mutationDraftEmptyAfter")}</Alert>
        )}
        {error && (
          <Alert severity={errorWarning ? "warning" : "error"}>{error}</Alert>
        )}
        {applied && (
          <Alert severity="success">
            {isMail
              ? t("chat.mail.sent")
              : openApi
                ? t("openapiCatalog.mutationApplied")
                : t("anomaly.mutationApplied")}
          </Alert>
        )}
        {applied && openApi && openApiExtra.applyResult && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              bgcolor: "action.hover",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 180,
              fontSize: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {openApiExtra.applyResult}
          </Box>
        )}
        {rejected && (
          <Alert severity="info">
            {isMail
              ? t("chat.mail.cancelled")
              : openApi
                ? t("openapiCatalog.mutationRejected")
                : t("anomaly.mutationRejected")}
          </Alert>
        )}
        {pending && (
          <Stack direction="row" spacing={1}>
            <AppButton
              variant="contained"
              color="primary"
              disabled={busy || !canApprove}
              onClick={() => void apply()}
            >
              {isMail ? t("chat.mail.approveSend") : t("anomaly.approve")}
            </AppButton>
            <AppButton variant="outlined" disabled={busy} onClick={() => void reject()}>
              {isMail ? t("chat.mail.reject") : t("anomaly.reject")}
            </AppButton>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
